import { Injectable } from '@angular/core';
import { IBarStyle, IScale } from 'src/app/interfaces';
import { GanttConfig } from './gantt-config.service';

@Injectable({
    providedIn: 'root'
})
export class GanttService {
    public rowHeight: number = 0;
    public hourCellWidth: number = 60; // change to 60 so minutes can been seen more easily
    public hoursCellWidth: number = this.hourCellWidth * 25;
    public cellWidth: number = 0;
    public windowInnerWidth: number = 0;
    public activityHeight: number = 0;
    public barHeight: number = 0;
    public barLineHeight: number = 0;
    public barTop: number = 0;
    public barMoveable: boolean = false;
    public gridWidth: number = 200;
    private barStyles: IBarStyle[] = [
        { status: "information", backgroundColor: "rgb(18,195, 244)", border: "1px solid #2196F3", progressBackgroundColor: "#2196F3" },
        { status: "warning", backgroundColor: "#FFA726", border: "1px solid #EF6C00", progressBackgroundColor: "#EF6C00" },
        { status: "error", backgroundColor: "#EF5350", border: "1px solid #C62828", progressBackgroundColor: "#C62828" },
        { status: "completed", backgroundColor: "#66BB6A", border: "1px solid #2E7D32", progressBackgroundColor: "#2E7D32" }
    ];
    public TASK_CACHE: any[];
    public TIME_SCALE: any[];
    public START_DATE: Date;

    public _MS_PER_DAY = 1000 * 60 * 60 * 24;

    constructor() {
        let _ganttConfig = new GanttConfig();

        this.rowHeight = _ganttConfig.rowHeight;
        this.cellWidth = _ganttConfig.cellWidth;

        //cellWidth / 24 should equal hour width
        this.hourCellWidth = this.cellWidth / 24;

        this.activityHeight = _ganttConfig.activityHeight;
        this.barHeight = _ganttConfig.barHeight;
        this.barLineHeight = _ganttConfig.barLineHeight;
        this.barTop = _ganttConfig.rowHeight;
        this.barMoveable = _ganttConfig.barMoveable;
    }

    private calculateBarWidth(start: Date, end: Date, hours?: boolean): number {
        if (typeof start === "string") {
            start = new Date(start);
        }

        if (typeof end === "string") {
            end = new Date(end);
        }

        if (Object.prototype.toString.call(end) === "[object Date]") {
            // it is a date
            if (isNaN(end.getTime())) { // d.getTime() or d.valueOf() will also work
                end = start;
            }
          } else {
            end = start;
          }

        let days = this.calculateDiffDays(start, end);
        let width: number = days * this.cellWidth + days;

        if (hours) {
            width = days * this.hourCellWidth * 24 + days * 24;
        }

        //do something better than this so it will display if it's start without an end date. This will be replaced with milestones
        if (width === 0)
        {
            width = 2;
        }

        return width;
    }

    private calculateBarLeft(start: Date, scale: any[], hours?: boolean): number {
        var left: number = 0;
        var hoursInDay: number = 24;

        if (start != null) {
            if (typeof start === "string") {
                start = new Date();
            }

            for (var i = 0; i < scale.length; i++) {
                if (start.getDate() === scale[i].getDate() && start.getMonth() === scale[i].getMonth() && start.getFullYear() == scale[i].getFullYear()) {
                    if (hours) {
                        left = i * hoursInDay * this.hourCellWidth + hoursInDay * i + this.calculateBarLeftDelta(start, hours) - i;
                    } else {
                        left = (i * this.cellWidth) + i + this.calculateBarLeftDelta(start, hours) - i;
                    }
                    break;
                }
            }
        }

        return left;
    }

    /** Calculates the height of the gantt grid, activity and vertical scroll */
    public calculateGanttHeight(): string {
        return `${(this.TASK_CACHE.length * this.rowHeight + this.rowHeight * 3) + 200}px`;
    }

    private calculateBarLeftDelta(start: Date, hours?: boolean): number {
        var offset: number = 0;
        var hoursInDay: number = 24;
        var minutesInHour: number = 60;
        var secondsInHour: number = 3600;
        var startHours: number = start.getHours() + start.getMinutes() / minutesInHour + start.getSeconds() / secondsInHour;

        if (hours) {
            offset = this.hoursCellWidth / hoursInDay * startHours - startHours;
        } else {
            //for each day minus 1px
            // var pixelOffSet = this.dateDiffInDays(start, this.START_DATE);

            //hardcode for demo but revisit
            var pixelOffSet = this.dateDiffInDays(start, start);
            offset = (this.cellWidth / hoursInDay * startHours) - pixelOffSet;
        }
        return offset;
    }

    // a and b are javascript Date objects
    dateDiffInDays(a: Date, b: Date) {
        // Discard the time and time-zone information.
        const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
        const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

        return Math.floor((utc2 - utc1) / this._MS_PER_DAY);
    }

    public isParent(treePath: string): boolean {

        try {
            var depth = treePath.split('/').length;

            if (depth === 1) {
                return true;
            }
        }
        catch (err) {
            return false;
        }
        return false;
    }

    public isChild(treePath: string) {
        if (this.isParent(treePath)) {
            return '0px';
        }
        return '20px';
    }

    /** Calculate the bar styles */
    public calculateBar(task: any, tasks: any[], index: number, rows: number, parentIndex: number, groupedEventsLength: number, scale: any, gridMarginHeight: number, hours?: boolean) {
        //var barStyle = this.getBarStyle(task.status);

        var marginTop = (25 * (rows)) + (25 * (parentIndex + 1)) + (26 * groupedEventsLength) + (26 * index) + gridMarginHeight;
        var tasks = tasks.sort(function (a, b) {
            return (a.eventTypeId!) - (b.eventTypeId!);
        });

        var barStyle;
        if (!!task.colour)
        {
            barStyle = task.colour;
        }

        var showMilestone = false;

        if (Object.prototype.toString.call(task.end) === "[object Date]") {
            // it is a date
            if (isNaN(task.end.getTime())) { // d.getTime() or d.valueOf() will also work
                task.end = task.start;
            }
          } else {
            task.end = task.start;
          }

        //We assign end to start elsewhere if end date wasn't set. Possible to remove this
        if (task.start.getTime() == task.end.getTime())
        {
            showMilestone = true;
        }

        //console.log(this.barTop * index + 2)
        return {
            'top': 2 + 'px',
            'left': this.calculateBarLeft(task.start, scale, hours) + 'px',
            'height': this.barHeight + 'px',
            'line-height': this.barLineHeight + 'px',
            'width': showMilestone ? this.barHeight + 'px' : this.calculateBarWidth(task.start, task.end, hours) + 'px',
            'background-color': !!barStyle ? barStyle : barStyle["background-color"],
            'border': !!barStyle ? `1px solid black` : barStyle["border"],
            'margin-top': marginTop + 'px',
            'transform': showMilestone ? 'rotate(45deg)' : '',
            'border-radius': showMilestone ? '0px' : '3px'
        }
    }

    /** Calculate the bar styles */
    // public calculateBarGrouped(task: any, tasks: CalendarEvent[], index: number, scale: any, hours?: boolean) {
    //     var barStyle = this.getBarStyle(task.status);

    //     var tasks = tasks.sort(function (a, b) {
    //         return (a.eventTypeId!) - (b.eventTypeId!);
    //     });

    //     //console.log(this.barTop * index + 2)
    //     return {
    //         'top': this.barTop * index + 2 + 'px',
    //         'left': this.calculateBarLeft(task.start, scale, hours) + 'px',
    //         'height': this.barHeight + 'px',
    //         'line-height': this.barLineHeight + 'px',
    //         'width': this.calculateBarWidth(task.start, task.end, hours) + 'px',
    //         'background-color': barStyle["background-color"],
    //         'border': barStyle["border"],
    //     }
    // }

    /** Calculate the bar styles */
    public calculateBarGrouped2(min: Date, max: Date, index: number, scale: any, primaryColour: string, hours?: boolean) {
        var barStyle = this.getBarStyle();

        var showMilestone = false;


        //console.log(this.barTop * index + 2)
        return {
            'top': this.barTop * index + 2 + 'px',
            // 'left': this.calculateBarLeft(min, scale, hours) + 'px',
            'height': showMilestone ? (this.barHeight + 10) + 'px' : (this.barHeight + 2) + 'px',
            'line-height': this.barLineHeight + 'px',
            'width': showMilestone ? (this.barHeight + 10) + 'px' : this.calculateBarWidth(min, max, hours) + 'px',
            'background-color': primaryColour,
            'transform': showMilestone ? 'rotate(45deg)' : '',
            'border-radius': showMilestone ? '0px' : '3px'
            // 'border': barStyle["border"],
        }
    }

        /** Calculate the bar styles */
        public calculateActivityLine(startEndEvent: any, index: number, scale: any, primaryColour: string, hours?: boolean) {
    
            var showMilestone = false;
            if (Object.prototype.toString.call(startEndEvent.end.end) === "[object Date]") {
                // it is a date
                if (isNaN(startEndEvent.end.end.getTime())) { // d.getTime() or d.valueOf() will also work
                    startEndEvent.end.end = startEndEvent.start.start;
                }
              } else {
                startEndEvent.end.end = startEndEvent.start.start;
              }
    
            //We assign end to start elsewhere if end date wasn't set. Possible to remove this
            if (startEndEvent.start.start == startEndEvent.end.end)
            {
                showMilestone = true;
            }
    
            //console.log(this.barTop * index + 2)
            return {
                'top': (this.barHeight/2) + this.barTop * index + 2 + 'px',
                'left': this.calculateBarLeft(startEndEvent.start.start, scale, hours) + 'px',
                // 'height': (this.barHeight + 2) + 'px',
                'height': showMilestone ? 10 + 'px' : 5 + 'px',
                'line-height': this.barLineHeight + 'px',
                'width': showMilestone ? 10 + 'px' : this.calculateBarWidth(startEndEvent.start.start, startEndEvent.end.end, hours) + 'px',
                'background-color': primaryColour,
                'border': primaryColour,
                'transform': showMilestone ? 'rotate(45deg)' : '',
                'border-radius': showMilestone ? '0px' : '3px',
                'margin-top': showMilestone ? '-4px' : '0px'
            }
        }

    /** Get the bar style based on task status */
    private getBarStyle(taskStatus: string = ""): any {
        var style = {};

        try {
            taskStatus = taskStatus.toLowerCase();
        } catch (err) {
            taskStatus = "";
        }

        switch (taskStatus) {
            case "information":
                style["background-color"] = this.barStyles[0].backgroundColor;
                style["border"] = this.barStyles[0].border;
                break;
            case "warning":
                style["background-color"] = this.barStyles[1].backgroundColor;
                style["border"] = this.barStyles[1].border;
                break;
            case "error":
                style["background-color"] = this.barStyles[2].backgroundColor;
                style["border"] = this.barStyles[2].border;
                break;
            case "completed":
                style["background-color"] = this.barStyles[3].backgroundColor;
                style["border"] = this.barStyles[3].border;
                break;
            default:
                style["background-color"] = "rgb(18,195, 244)";
                style["border"] = "1px solid #2196F3";
                break;
        }

        return style;
    }

    /** Get the progresss bar background colour based on task status */
    public getBarProgressStyle(taskStatus: string = ""): any {
        var style = {};

        try {
            taskStatus = taskStatus.toLowerCase();
        } catch (err) {
            taskStatus = "";
        }

        switch (taskStatus) {
            case "information":
                style["background-color"] = this.barStyles[0].progressBackgroundColor;
                break;
            case "warning":
                style["background-color"] = this.barStyles[1].progressBackgroundColor;
                break;
            case "error":
                style["background-color"] = this.barStyles[2].progressBackgroundColor;
                break;
            case "completed":
                style["background-color"] = this.barStyles[3].progressBackgroundColor;
                break;
            default:
                style["background-color"] = this.barStyles[0].progressBackgroundColor;
                break;
        }

        return style;
    }

    /** Calculates the bar progress width in pixels given task percent complete */
    public calculateBarProgress(width: number, percent: number): string {
        if (typeof percent === "number") {
            if (percent > 100) {
                percent = 100;
            }
            let progress: number = (width / 100) * percent - 2;

            return `${progress}px`;
        }
        return `${0}px`;
    }

    /** Calculates the difference in two dates and returns number of days */
    public calculateDiffDays(start: Date, end: Date): number {
        try {
            let oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds /ms
            let diffDays = Math.abs((start.getTime() - end.getTime()) / (oneDay));
            let days = diffDays; // don't use Math.round as it will draw an incorrect bar

            return days;
        } catch (err) {
            return 0;
        }
    }

    /** Calculates the difference in two dates and returns number of hours */
    public calculateDuration(task: any): string {
        try {
            if (task.start != null && task.end != null) {
                let oneHour = 60 * 60 * 1000;
                let diffHours = (Math.abs((task.start.getTime() - task.end.getTime()) / oneHour));
                let duration = diffHours;

                if (duration > 24) {
                    return `${Math.round(duration / 24)} day(s)`; // duration in days
                } else if (duration > 1) {
                    return `${Math.round(duration)} hr(s)`; // duration in hours
                } else {
                    let minutes = duration * 60;

                    if (minutes < 1) {
                        return `${Math.round(minutes * 60)} second(s)`; // duration in seconds
                    }
                    return `${Math.round(minutes)} min(s)` // duration in minutes
                }
            }

            return '';
        } catch (err) {
            return '';
        }
    }

    calculateTotalDuration(tasks: any[]): string {
        try {
            tasks = tasks.filter(t => t.parentId === t.id); // only calculate total duration with parent tasks

            let totalHours = 0;
            let oneHour = 60 * 60 * 1000;
            for (let i = 0; i < tasks.length; i++) {
                let start = tasks[i].start;
                let end = tasks[i].end;

                if (start != null && end != null) {
                    let duration = Math.abs(tasks[i].end.getTime() - tasks[i].start.getTime()) / oneHour; // duration in hours
                    totalHours += duration;
                }
            }

            if (totalHours === 0) {
                return '';
            }

            if (totalHours > 24) {
                return `${Math.round(totalHours / 24)} day(s)`; // duration in days
            } else if (totalHours > 1) {
                return `${Math.round(totalHours)} hr(s)`; // duration in hours
            } else {
                let minutes = totalHours * 60;

                if (minutes < 1) {
                    return `${Math.round(minutes * 60)} second(s)`; // duration in seconds
                }
                return `${Math.round(minutes)} min(s)` // duration in minutes
            }
        } catch (err) {
            return '';
        }
    }

    /** Calculate the total percentage of a group of tasks */
    // calculateTotalPercentage(node: any): number {
    //     var totalPercent: number = 0;
    //     var children = node.children;

    //     if (children.length > 0) {
    //         children.forEach((child: any) => {
    //             totalPercent += isNaN(child.percentComplete) ? 0 : child.percentComplete;
    //         });

    //         return Math.ceil(totalPercent / children.length);
    //     } else {
    //         return isNaN(node.percentComplete) ? 0 : node.percentComplete;
    //     }
    // }

    /** Calculate the total percent of the parent task */
    // calculateParentTotalPercentage(parent: any, tasks: any[]) {
    //     var children = tasks.filter((task:any) => {
    //         return task.parentId === parent.id && task.id != parent.id
    //     }); // get only children tasks ignoring parent.

    //     var totalPercent: number = 0;

    //     if (children.length > 0) {
    //         children.forEach((child:any) => {
    //             totalPercent += isNaN(child.percentComplete) ? 0 : child.percentComplete;
    //         });

    //         return Math.ceil(totalPercent / children.length);
    //     } else {
    //         return isNaN(parent.percentComplete) ? 0 : parent.percentComplete;
    //     }
    // }

    /** Calculate the gantt scale range given the start and end date of tasks*/
    public calculateScale(start: Date = new Date(), end: Date = this.addDays(start, 7)) {
        let scale: any[] = [];

        try {
            if (!end)
            {
                scale.push(start);
                start = this.addDays(start, 1);
            }
            else {
                while (start.getTime() <= end.getTime()) {
                    scale.push(start);
                    start = this.addDays(start, 1);
                }
            }
            return scale;

        } catch (err) {
            return scale;
        }
    }

    /** Determines whether given date is a weekend */
    public isDayWeekend(date: Date): boolean {
        let day = date.getDay();

        if (day === 6 || day === 0) {
            return true;
        }
        return false;
    }

    /** Add x number of days to a date object */
    public addDays(date: Date, days: number): Date {
        let result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    //** Remove x number of days from a date object */
    public removeDays(date: Date, days: number): Date {
        let result = new Date(date);
        result.setDate(result.getDate() - days);
        return result;
    }

    /** Calculates the grid scale for gantt based on tasks start and end dates */
    public calculateGridScale(events: any[]): IScale {
        var start: Date;
        var end: Date;

        var allStartDates = events.map(x => x.start.getTime());
        var allEndDates = events.map(x => x.end.getTime()) as Array<number>;

        var dates = events.map((task: any) => {
            return {
                start: new Date(task.start),
                end: new Date(task.end)
            }
        });

        // start = new Date(Math.min.apply(null, dates.map(function (t) {
        //     return t.start;
        // })));

        // end = new Date(Math.max.apply(null, dates.map(function (t) {
        //     return t.end;
        // })));

        start = new Date(Math.min.apply(null, allStartDates));
        end = new Date(Math.max.apply(null, allEndDates));

        return {
            start: start,
            end: end
        }
    }

    /** Create an hours array for use in time scale component */
    public getHours(cols: number): string[] {
        var hours: string[] = [];

        while (hours.length <= cols * 24) {
            for (var i = 0; i <= 23; i++) {
                if (i < 10) {
                    hours.push('0' + i.toString());
                } else {
                    hours.push(i.toString());
                }
            }
        }

        return hours;
    }

    public getComputedStyle(element: any, attribute: any) {
        return parseInt(document.defaultView!.getComputedStyle(element)[attribute], 10);
    }

    //TODO(dale): determine whether this is needed
    public calculateContainerWidth(): number {
        this.windowInnerWidth = window.innerWidth;
        let containerWidth = (innerWidth - 18);

        return containerWidth;
    }

    public calculateActivityContainerDimensions(): any {
        var scrollWidth: number = 0; //18
        this.windowInnerWidth = 1296;
        let width = this.windowInnerWidth - this.gridWidth + 30;

        return { height: this.activityHeight, width: width };
    }

    /** Set the vertical scroll top positions for gantt */
    public scrollTop(verticalScrollElem: any, ganttGridElem: any, ganttActivityAreaElem: any) {
        let verticalScrollTop = verticalScrollElem.scrollTop;
        let scroll = this.setScrollTop;

        // debounce
        if (verticalScrollTop !== null && verticalScrollTop !== undefined) {
            setTimeout(function () {
                scroll(verticalScrollTop, ganttActivityAreaElem);
                scroll(ganttActivityAreaElem.scrollTop, ganttGridElem);

            }, 50);
        }
    }

    /** Group data by id , only supports one level*/
    public groupData(tasks: any): any {
        // var merged:any = [];
        // var groups:any = new GroupByPipe().transform(tasks, (task:any) => {
        //     return [task.treePath.split('/')[0]]
        // });
        // return merged.concat.apply([], groups);
        return tasks;
    }

    /** Create tree of data */
    public transformData(input: any): any {
        var output: any = [];
        for (var i = 0; i < input.length; i++) {
            var chain: any = input[i].id.split('/');
            var currentNode: any = output;
            for (var j = 0; j < chain.length; j++) {
                var wantedNode: any = chain[j];
                var lastNode: any = currentNode;
                for (var k = 0; k < currentNode.length; k++) {
                    if (currentNode[k].name == wantedNode) {
                        currentNode = currentNode[k].children;
                        break;
                    }
                }
                // If we couldn't find an item in this list of children
                // that has the right name, create one:
                if (lastNode == currentNode) {
                    //TODO(dale): determine way to show percent complete on correct child  
                    var newNode: any = currentNode[k] = {
                        name: wantedNode,
                        // percentComplete: input[i].percentComplete,
                        start: input[i].start,
                        end: input[i].end,
                        children: []
                    };
                    currentNode = newNode.children;
                }
            }
        }
        return output;
    }

    /** Checks whether any new data needs to be added to task cache  */
    public doTaskCheck(tasks: any[], treeExpanded: boolean): boolean {
        var cachedTaskIds = this.TASK_CACHE.map((task: any) => { return task.id });
        var itemsToCache: any[] = [];

        if (!!tasks && tasks.length > 0) {
            if (treeExpanded) {
                // push children and parent tasks that are not cached
                tasks.filter((task: any) => {
                    return cachedTaskIds.indexOf(task.id) === -1
                }).forEach((task: any) => {
                    itemsToCache.push(task);
                })
            } else {
                // only look at tasks that are not cached
                tasks.filter((task: any) => {
                    return cachedTaskIds.indexOf(task.id) === -1 && task.treePath.split('/').length === 1
                }).forEach((task: any) => {
                    itemsToCache.push(task);
                });
            }
        }

        itemsToCache.forEach((item: any) => {
            this.TASK_CACHE.push(item);
        });

        if (itemsToCache.length > 0) {
            return true;
        }

        return false;
    }

    /** Set a id prefix so CSS3 query selector can work with ids that contain numbers */
    public setIdPrefix(id: string): string {
        return `_${id}`;
    }

    // /** Remove the id prefix to allow querying of data */
    // public removeIdPrefix(id: string): string {
    //     return id.substring(1, id.length - 1);
    // }

    /** Set the scroll top property of a native DOM element */
    private setScrollTop(scrollTop: number, element: any): void {
        if (element !== null && element !== undefined) {
            element.scrollTop = scrollTop;
        }
    }
}
